import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Mail,
  Phone,
  Shield,
  SlidersHorizontal,
  Star,
  Clock,
  Plus,
  Pencil,
  Trash2,
  CreditCard,
  Gift,
  Users,
  MessageSquare,
  Zap,
  Activity,
  Download,
  StickyNote,
  Fingerprint,
  ChevronRight,
  ChevronDown,
  X,
  ShieldOff,
  Tag,
  BarChart2,
  Upload,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import {
  extractError,
  type CustomerDetail,
  type CustomerContact,
  type CustomerConsent,
  type CustomerLoyaltyAccount,
  type CustomerSegment,
  type CustomerIdentity,
  type EngagementScore,
  customerFullName,
} from '../lib/types.ts';
import { formatDate, formatDateTime, formatRelative } from '../lib/utils.ts';
import { Button } from '../components/ui/Button.tsx';
import { Badge, CustomerStatusBadge, ChannelBadge } from '../components/ui/Badge.tsx';
import { Skeleton } from '../components/ui/Skeleton.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input, Select, Textarea } from '../components/ui/Input.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { Pagination } from '../components/ui/Pagination.tsx';
import { useToast } from '../components/ui/Toast.tsx';
import { cn } from '../components/ui/cn.ts';

// ─── Types ────────────────────────────────────────────────────────────────────

// Core customer types are imported from lib/types.ts.
// Local alias for convenience within this file.
type Customer = CustomerDetail;
type Contact = CustomerContact;
type Consent = CustomerConsent;
type LoyaltyAccount = CustomerLoyaltyAccount;
// CustomerSegment, CustomerIdentity used directly (no alias needed)

interface LedgerEntry {
  id: string;
  type: string;
  points: number;
  balanceAfter: number;
  referenceType: string | null;
  referenceId: string | null;
  description: string | null;
  occurredAt: string;
}
interface Coupon {
  id: string;
  code: string;
  type: string;
  value: { amount: number; unit: string };
  status: string;
  issuedAt: string;
  expiresAt: string | null;
}
interface TimelineEvent {
  id: string;
  eventCategory: string;
  eventType: string;
  title: string;
  description: string | null;
  occurredAt: string;
  referenceType: string | null;
  referenceId: string | null;
}
interface MessageLog {
  id: string;
  channel: string;
  subject: string | null;
  recipientAddress: string;
  providerName: string;
  status: string;
  requestedAt: string;
  sentAt: string | null;
  sourceCampaignId: string | null;
  errorMessage: string | null;
}
interface EventInbox {
  id: string;
  eventType: string;
  source: string;
  payloadJson: unknown;
  occurredAt: string;
  processedAt: string | null;
  processingError: string | null;
}
interface AutomationInstance {
  instanceId: string;
  automationId: string;
  automationName: string;
  status: string;
  currentStep: string | null;
  startedAt: string;
  endedAt: string | null;
  errorMessage: string | null;
}
interface Identity {
  id: string;
  identityType: string;
  identityValue: string;
  createdAt: string;
}
interface CustomerNote {
  id: string;
  body: string;
  createdBy: string | null;
  createdAt: string;
}

interface PropertyDefinition {
  id: string;
  key: string;
  label: string;
  fieldType: 'text' | 'number' | 'boolean' | 'date' | 'email' | 'url' | 'select' | 'multiselect';
  isRequired: boolean;
  description: string | null;
  options: string[];
  sortOrder: number;
  isActive: boolean;
}

interface SuppressionStatus {
  email: string;
  isSuppressed: boolean;
  reason: string | null;
  suppressedAt: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// fullName, fmt, fmtTime, fmtRelative replaced by shared helpers from lib/utils.ts and lib/types.ts

const CATEGORY_COLORS: Record<string, string> = {
  loyalty: 'bg-amber-100 text-amber-700',
  automation: 'bg-violet-100 text-violet-700',
  message: 'bg-sky-100 text-sky-700',
  campaign: 'bg-emerald-100 text-emerald-700',
};
const CONSENT_STATUS_MAP: Record<
  string,
  { label: string; variant: 'success' | 'danger' | 'warning' | 'default' }
> = {
  granted: { label: 'Granted', variant: 'success' },
  revoked: { label: 'Revoked', variant: 'danger' },
  pending: { label: 'Pending', variant: 'warning' },
  unknown: { label: 'Unknown', variant: 'default' },
};
const INSTANCE_STATUS: Record<string, { label: string; cls: string }> = {
  running: { label: 'Running', cls: 'bg-emerald-100 text-emerald-700' },
  waiting: { label: 'Waiting', cls: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Completed', cls: 'bg-zinc-100 text-zinc-600' },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelled', cls: 'bg-orange-100 text-orange-700' },
};

// ─── Reusable section wrapper ─────────────────────────────────────────────────

function Section({
  title,
  icon,
  children,
  action,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-b border-zinc-100">
        <div className="flex items-center gap-2">
          <span className="text-zinc-400">{icon}</span>
          <h2 className="text-sm font-semibold text-zinc-700">{title}</h2>
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-zinc-50 last:border-0">
      <span className="text-xs text-zinc-500 shrink-0">{label}</span>
      <span className="text-xs text-zinc-800 font-medium text-right break-all">{value ?? '—'}</span>
    </div>
  );
}

// ─── Tab types ────────────────────────────────────────────────────────────────

type Tab = 'timeline' | 'ledger' | 'coupons' | 'segments' | 'messages' | 'events' | 'automations';

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'timeline', label: 'Timeline', icon: <Clock size={13} /> },
  { id: 'ledger', label: 'Points', icon: <CreditCard size={13} /> },
  { id: 'coupons', label: 'Coupons', icon: <Gift size={13} /> },
  { id: 'segments', label: 'Segments', icon: <Users size={13} /> },
  { id: 'messages', label: 'Messages', icon: <MessageSquare size={13} /> },
  { id: 'events', label: 'Events', icon: <Activity size={13} /> },
  { id: 'automations', label: 'Automations', icon: <Zap size={13} /> },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState<Tab>('timeline');
  const [timelinePage, setTimelinePage] = useState(1);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [messagesPage, setMessagesPage] = useState(1);
  const [eventsPage, setEventsPage] = useState(1);
  const [automationsPage, setAutomationsPage] = useState(1);
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const pagLimit = 25; // used for all paginated tab queries

  // ── modals state ──────────────────────────────────────────────────────────────

  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editProfileTab, setEditProfileTab] = useState<'personal' | 'location' | 'professional'>(
    'personal',
  );
  const [editProfileForm, setEditProfileForm] = useState({
    firstName: '',
    lastName: '',
    status: 'active',
    dateOfBirth: '',
    gender: '',
    language: '',
    country: '',
    city: '',
    timezone: '',
    avatarUrl: '',
    acquisitionSource: '',
    acquisitionDate: '',
    company: '',
    jobTitle: '',
    addressLine1: '',
    addressLine2: '',
    postalCode: '',
    stateProvince: '',
  });

  // Tags
  const [showTagInput, setShowTagInput] = useState(false);
  const [tagInputValue, setTagInputValue] = useState('');

  // Engagement score
  const [showEngagementScore, setShowEngagementScore] = useState(false);

  const [showAddContact, setShowAddContact] = useState<'add' | 'edit' | false>(false);
  const [contactForm, setContactForm] = useState({ email: '', phone: '', isPrimary: false });

  const [showAddConsent, setShowAddConsent] = useState(false);
  const [consentForm, setConsentForm] = useState({
    channel: 'email',
    status: 'granted',
    source: '',
  });

  const [showAddAttr, setShowAddAttr] = useState(false);
  const [attrForm, setAttrForm] = useState({ key: '', value: '' });

  const [showAddIdentity, setShowAddIdentity] = useState(false);
  const [identityForm, setIdentityForm] = useState({ identityType: '', identityValue: '' });

  const [showAddNote, setShowAddNote] = useState(false);
  const [noteBody, setNoteBody] = useState('');

  const [showIssueCoupon, setShowIssueCoupon] = useState(false);
  const [couponForm, setCouponForm] = useState({
    type: '',
    amount: '',
    unit: 'percent',
    expiresInDays: '',
  });

  const [blockConfirm, setBlockConfirm] = useState(false);
  const [deleteIdentityTarget, setDeleteIdentityTarget] = useState<Identity | null>(null);
  const [deleteNoteTarget, setDeleteNoteTarget] = useState<CustomerNote | null>(null);
  const [revokeCouponTarget, setRevokeCouponTarget] = useState<Coupon | null>(null);

  // ── queries ───────────────────────────────────────────────────────────────────

  const {
    data: customer,
    isLoading,
    isError,
    error,
  } = useQuery<Customer>({
    queryKey: ['customer', id],
    queryFn: () => api.get(`/api/v1/customers/${id}`).then((r) => r.data),
    enabled: !!id,
  });

  const { data: ledgerData } = useQuery<{ items: LedgerEntry[]; total?: number }>({
    queryKey: ['customer-ledger', id, ledgerPage],
    queryFn: () =>
      api
        .get(`/api/v1/loyalty/ledger/${id}`, { params: { page: ledgerPage, limit: pagLimit } })
        .then((r) => r.data),
    enabled: !!id && activeTab === 'ledger',
  });

  const { data: couponsData } = useQuery<Coupon[]>({
    queryKey: ['customer-coupons', id],
    queryFn: () => api.get(`/api/v1/loyalty/coupons/${id}`).then((r) => r.data),
    enabled: !!id && activeTab === 'coupons',
  });

  const { data: timelineData } = useQuery<{ items: TimelineEvent[]; total: number }>({
    queryKey: ['customer-timeline', id, timelinePage],
    queryFn: () =>
      api
        .get(`/api/v1/customers/${id}/timeline`, {
          params: { limit: pagLimit, offset: (timelinePage - 1) * pagLimit },
        })
        .then((r) => r.data),
    enabled: !!id && activeTab === 'timeline',
  });

  const { data: messagesData } = useQuery<{ items: MessageLog[]; total: number }>({
    queryKey: ['customer-messages', id, messagesPage],
    queryFn: () =>
      api
        .get(`/api/v1/customers/${id}/messages`, {
          params: { page: messagesPage, limit: pagLimit },
        })
        .then((r) => r.data),
    enabled: !!id && activeTab === 'messages',
  });

  const { data: eventsData } = useQuery<{ items: EventInbox[]; total: number }>({
    queryKey: ['customer-events', id, eventsPage, eventTypeFilter],
    queryFn: () =>
      api
        .get(`/api/v1/customers/${id}/events`, {
          params: {
            page: eventsPage,
            limit: pagLimit,
            ...(eventTypeFilter ? { eventType: eventTypeFilter } : {}),
          },
        })
        .then((r) => r.data),
    enabled: !!id && activeTab === 'events',
  });

  const { data: automationsData } = useQuery<{ items: AutomationInstance[]; total: number }>({
    queryKey: ['customer-automations', id, automationsPage],
    queryFn: () =>
      api
        .get(`/api/v1/customers/${id}/automations`, {
          params: { page: automationsPage, limit: pagLimit },
        })
        .then((r) => r.data),
    enabled: !!id && activeTab === 'automations',
  });

  const { data: notesData, isLoading: notesLoading } = useQuery<CustomerNote[]>({
    queryKey: ['customer-notes', id],
    queryFn: () => api.get(`/api/v1/customers/${id}/notes`).then((r) => r.data),
    enabled: !!id,
  });

  const { data: propertyDefs = [] } = useQuery<PropertyDefinition[]>({
    queryKey: ['property-definitions'],
    queryFn: () => api.get('/api/v1/settings/properties').then((r) => r.data),
    enabled: !!id,
    staleTime: 60_000,
  });

  const { data: suppressionData, refetch: refetchSuppressions } = useQuery<SuppressionStatus[]>({
    queryKey: ['customer-suppressions', id],
    queryFn: () => api.get(`/api/v1/customers/${id}/suppression-status`).then((r) => r.data),
    enabled: !!id,
    staleTime: 30_000,
  });

  // Tags query (also included in customer detail, but cached separately for mutations)
  const { data: allTagsData = [] } = useQuery<string[]>({
    queryKey: ['customer-tags-all'],
    queryFn: () => api.get('/api/v1/customers/tags').then((r) => r.data),
    staleTime: 60_000,
  });

  // Engagement score (lazy — only loaded when user clicks "Calculate")
  const {
    data: engagementScore,
    isFetching: engagementLoading,
    refetch: refetchEngagement,
  } = useQuery<EngagementScore>({
    queryKey: ['customer-engagement', id],
    queryFn: () => api.get(`/api/v1/customers/${id}/engagement-score`).then((r) => r.data),
    enabled: showEngagementScore,
    staleTime: 5 * 60_000,
  });

  // ── mutations ─────────────────────────────────────────────────────────────────

  const invalidate = () => qc.invalidateQueries({ queryKey: ['customer', id] });

  type UpdateProfileBody = Omit<
    typeof editProfileForm,
    | 'dateOfBirth'
    | 'gender'
    | 'language'
    | 'country'
    | 'city'
    | 'timezone'
    | 'avatarUrl'
    | 'acquisitionSource'
    | 'acquisitionDate'
    | 'company'
    | 'jobTitle'
    | 'addressLine1'
    | 'addressLine2'
    | 'postalCode'
    | 'stateProvince'
  > & {
    dateOfBirth: string | null;
    gender: string | null;
    language: string | null;
    country: string | null;
    city: string | null;
    timezone: string | null;
    avatarUrl: string | null;
    acquisitionSource: string | null;
    acquisitionDate: string | null;
    company: string | null;
    jobTitle: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    postalCode: string | null;
    stateProvince: string | null;
  };
  const updateProfile = useMutation({
    mutationFn: (body: UpdateProfileBody) => api.patch(`/api/v1/customers/${id}`, body),
    onSuccess: () => {
      invalidate();
      setShowEditProfile(false);
      toast.success('Profile updated');
    },
    onError: (e) => toast.error(extractError(e)),
  });

  const upsertContact = useMutation({
    mutationFn: (body: typeof contactForm) => api.put(`/api/v1/customers/${id}/contacts`, body),
    onSuccess: () => {
      invalidate();
      setShowAddContact(false);
      toast.success('Contact saved');
    },
    onError: (e) => toast.error(extractError(e)),
  });

  const recordConsent = useMutation({
    mutationFn: (body: typeof consentForm) => api.post(`/api/v1/customers/${id}/consents`, body),
    onSuccess: () => {
      invalidate();
      setShowAddConsent(false);
      toast.success('Consent recorded');
    },
    onError: (e) => toast.error(extractError(e)),
  });

  const setAttribute = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      api.put(`/api/v1/customers/${id}/attributes/${encodeURIComponent(key)}`, { value }),
    onSuccess: () => {
      invalidate();
      setShowAddAttr(false);
      toast.success('Attribute saved');
    },
    onError: (e) => toast.error(extractError(e)),
  });

  const addIdentity = useMutation({
    mutationFn: (body: typeof identityForm) => api.post(`/api/v1/customers/${id}/identities`, body),
    onSuccess: () => {
      invalidate();
      setShowAddIdentity(false);
      setIdentityForm({ identityType: '', identityValue: '' });
      toast.success('Identity added');
    },
    onError: (e) => toast.error(extractError(e)),
  });

  const deleteIdentity = useMutation({
    mutationFn: (identityId: string) =>
      api.delete(`/api/v1/customers/${id}/identities/${identityId}`),
    onSuccess: () => {
      invalidate();
      setDeleteIdentityTarget(null);
      toast.success('Identity removed');
    },
    onError: (e) => toast.error(extractError(e)),
  });

  const addNote = useMutation({
    mutationFn: (body: string) => api.post(`/api/v1/customers/${id}/notes`, { body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer-notes', id] });
      setNoteBody('');
      setShowAddNote(false);
      toast.success('Note added');
    },
    onError: (e) => toast.error(extractError(e)),
  });

  const deleteNote = useMutation({
    mutationFn: (noteId: string) => api.delete(`/api/v1/customers/${id}/notes/${noteId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer-notes', id] });
      setDeleteNoteTarget(null);
      toast.success('Note deleted');
    },
    onError: (e) => toast.error(extractError(e)),
  });

  const issueCoupon = useMutation({
    mutationFn: () =>
      api.post('/api/v1/loyalty/coupons/issue', {
        customerId: id,
        type: couponForm.type,
        value: { amount: Number(couponForm.amount), unit: couponForm.unit },
        expiresInDays: couponForm.expiresInDays ? Number(couponForm.expiresInDays) : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer-coupons', id] });
      setShowIssueCoupon(false);
      setCouponForm({ type: '', amount: '', unit: 'percent', expiresInDays: '' });
      toast.success('Coupon issued');
    },
    onError: (e) => toast.error(extractError(e)),
  });

  const revokeCoupon = useMutation({
    mutationFn: (couponId: string) => api.patch(`/api/v1/loyalty/coupons/${couponId}/revoke`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer-coupons', id] });
      setRevokeCouponTarget(null);
      toast.success('Coupon revoked');
    },
    onError: (e) => toast.error(extractError(e)),
  });

  const blockMutation = useMutation({
    mutationFn: () =>
      api.patch(`/api/v1/customers/${id}`, {
        status: customer?.status === 'blocked' ? 'active' : 'blocked',
      }),
    onSuccess: () => {
      invalidate();
      setBlockConfirm(false);
    },
    onError: (e) => toast.error(extractError(e)),
  });

  const removeSuppression = useMutation({
    mutationFn: (email: string) =>
      api.delete(`/api/v1/settings/suppressions/${encodeURIComponent(email)}`),
    onSuccess: () => {
      void refetchSuppressions();
      toast.success('Suppression removed');
    },
    onError: (e) => toast.error(extractError(e)),
  });

  const addTag = useMutation({
    mutationFn: (tag: string) =>
      api.post(`/api/v1/customers/${id}/tags/${encodeURIComponent(tag)}`),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['customer-tags-all'] });
      setTagInputValue('');
      setShowTagInput(false);
    },
    onError: (e) => toast.error(extractError(e)),
  });

  const removeTag = useMutation({
    mutationFn: (tag: string) =>
      api.delete(`/api/v1/customers/${id}/tags/${encodeURIComponent(tag)}`),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['customer-tags-all'] });
    },
    onError: (e) => toast.error(extractError(e)),
  });

  // ── derived ───────────────────────────────────────────────────────────────────

  const contacts = customer?.contacts ?? [];
  const consents = customer?.consents ?? [];
  const attributes = customer?.attributes ?? {};
  const loyaltyAccounts = customer?.loyaltyAccounts ?? [];
  const segments = customer?.segments ?? [];
  const identities = customer?.identities ?? [];
  const tags = customer?.tags ?? [];

  // KPI: days since last timeline event
  const lastEventDays = timelineData?.items?.[0]
    ? Math.floor((Date.now() - new Date(timelineData.items[0].occurredAt).getTime()) / 86_400_000)
    : null;

  if (isLoading) {
    return (
      <div className="p-8 space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="space-y-4">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
          <div className="lg:col-span-2">
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
      </div>
    );
  }

  // Distinguish 404 (customer deleted or wrong ID) from other errors so the admin
  // gets a meaningful message instead of a blank/skeleton page.
  if (isError || !customer) {
    const is404 =
      isError && (error as { response?: { status?: number } })?.response?.status === 404;
    return (
      <div className="p-8 flex flex-col items-center justify-center gap-3 text-center">
        <p className="text-lg font-semibold text-zinc-700">
          {is404 ? 'Customer not found' : 'Failed to load customer'}
        </p>
        <p className="text-sm text-zinc-400">
          {is404
            ? 'This customer may have been deleted or the URL is incorrect.'
            : 'An error occurred while fetching customer data. Please try again.'}
        </p>
        <button
          onClick={() => navigate('/customers')}
          className="mt-2 text-sm text-zinc-500 hover:text-zinc-800 underline"
        >
          ← Back to Customers
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5">
      {/* ── Header ── */}
      <div className="flex items-center gap-4 flex-wrap">
        <button
          onClick={() => navigate('/customers')}
          className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 transition-colors"
        >
          <ArrowLeft size={16} /> Customers
        </button>
        <div className="flex-1 flex items-center gap-3 flex-wrap">
          {/* Avatar */}
          {customer.avatarUrl ? (
            <img
              src={customer.avatarUrl}
              alt={customerFullName(customer)}
              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-semibold text-zinc-600">
                {((customer.firstName?.[0] ?? '') + (customer.lastName?.[0] ?? '')).toUpperCase() ||
                  '?'}
              </span>
            </div>
          )}
          <h1 className="text-xl font-bold text-zinc-900">{customerFullName(customer)}</h1>
          <CustomerStatusBadge status={customer.status} />
          {customer.externalId && (
            <code className="text-xs bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded font-mono">
              {customer.externalId}
            </code>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setEditProfileForm({
                firstName: customer.firstName ?? '',
                lastName: customer.lastName ?? '',
                status: customer.status,
                dateOfBirth: customer.dateOfBirth ?? '',
                gender: customer.gender ?? '',
                language: customer.language ?? '',
                country: customer.country ?? '',
                city: customer.city ?? '',
                timezone: customer.timezone ?? '',
                avatarUrl: customer.avatarUrl ?? '',
                acquisitionSource: customer.acquisitionSource ?? '',
                acquisitionDate: customer.acquisitionDate
                  ? customer.acquisitionDate.slice(0, 10)
                  : '',
                company: customer.company ?? '',
                jobTitle: customer.jobTitle ?? '',
                addressLine1: customer.addressLine1 ?? '',
                addressLine2: customer.addressLine2 ?? '',
                postalCode: customer.postalCode ?? '',
                stateProvince: customer.stateProvince ?? '',
              });
              setEditProfileTab('personal');
              setShowEditProfile(true);
            }}
          >
            <Pencil size={12} className="mr-1" /> Edit
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              const url = `/api/v1/customers/${id}/export`;
              const a = document.createElement('a');
              api.get(url, { responseType: 'blob' }).then((r) => {
                const blob = new Blob([r.data], { type: 'application/json' });
                const objUrl = URL.createObjectURL(blob);
                a.href = objUrl;
                a.download = `customer-${id}.json`;
                a.click();
                URL.revokeObjectURL(objUrl);
              });
            }}
          >
            <Download size={12} className="mr-1" /> Export
          </Button>
          <Button
            size="sm"
            variant={customer.status === 'blocked' ? 'secondary' : 'danger'}
            onClick={() => setBlockConfirm(true)}
          >
            {customer.status === 'blocked' ? 'Unblock' : 'Block'}
          </Button>
        </div>
      </div>

      {/* ── Main grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        {/* ══ Left sidebar ══ */}
        <div className="space-y-4">
          {/* Identity */}
          <Section title="Identity" icon={<Shield size={15} />}>
            <KV
              label="Customer ID"
              value={<code className="text-[11px] font-mono">{customer.id}</code>}
            />
            <KV label="Status" value={<CustomerStatusBadge status={customer.status} />} />
            <KV label="Created" value={formatDate(customer.createdAt)} />
            <KV label="Updated" value={formatDate(customer.updatedAt)} />
            {customer.dateOfBirth && <KV label="Date of birth" value={customer.dateOfBirth} />}
            {customer.gender && <KV label="Gender" value={customer.gender} />}
            {customer.company && <KV label="Company" value={customer.company} />}
            {customer.jobTitle && <KV label="Job title" value={customer.jobTitle} />}
            {customer.city && <KV label="City" value={customer.city} />}
            {customer.stateProvince && <KV label="State/Province" value={customer.stateProvince} />}
            {customer.postalCode && <KV label="Postal code" value={customer.postalCode} />}
            {customer.country && <KV label="Country" value={customer.country} />}
            {customer.addressLine1 && <KV label="Address" value={customer.addressLine1} />}
            {customer.addressLine2 && <KV label="" value={customer.addressLine2} />}
            {customer.language && <KV label="Language" value={customer.language} />}
            {customer.timezone && <KV label="Timezone" value={customer.timezone} />}
            {customer.acquisitionSource && (
              <KV label="Acq. source" value={customer.acquisitionSource} />
            )}
            {customer.acquisitionDate && (
              <KV label="Acq. date" value={formatDate(customer.acquisitionDate)} />
            )}
          </Section>

          {/* KPI Score */}
          <Section title="Activity" icon={<Activity size={15} />}>
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center bg-zinc-50 rounded-lg p-3">
                <p className="text-lg font-bold text-zinc-800">
                  {lastEventDays !== null ? lastEventDays : '—'}
                </p>
                <p className="text-[10px] text-zinc-400 mt-0.5">Days inactive</p>
              </div>
              <div className="text-center bg-zinc-50 rounded-lg p-3">
                <p className="text-lg font-bold text-zinc-800">
                  {loyaltyAccounts[0]?.pointsBalance ?? '—'}
                </p>
                <p className="text-[10px] text-zinc-400 mt-0.5">Points</p>
              </div>
              <div className="text-center bg-zinc-50 rounded-lg p-3">
                <p className="text-lg font-bold text-zinc-800">{segments.length}</p>
                <p className="text-[10px] text-zinc-400 mt-0.5">Segments</p>
              </div>
            </div>
          </Section>

          {/* Contacts */}
          <Section
            title="Contacts"
            icon={<Mail size={15} />}
            action={
              <button
                onClick={() => {
                  // "+" always opens an empty form — adding a new contact.
                  // To edit the existing primary contact the user clicks its
                  // email/phone row (handled further down in the contacts list).
                  setContactForm({ email: '', phone: '', isPrimary: true });
                  setShowAddContact('add');
                }}
                className="p-1 rounded hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700"
              >
                <Plus size={14} />
              </button>
            }
          >
            {contacts.length === 0 ? (
              <p className="text-xs text-zinc-400 italic">No contacts</p>
            ) : (
              <div className="space-y-2">
                {contacts.map((c) => (
                  <div key={c.id} className="text-xs space-y-1 group/contact">
                    <div className="flex items-start justify-between gap-1">
                      <div className="space-y-1 min-w-0">
                        {c.email && (
                          <div className="flex items-center gap-1.5">
                            <Mail size={11} className="text-zinc-400 shrink-0" />
                            <span className="text-zinc-700 truncate">{c.email}</span>
                            {c.emailVerifiedAt && (
                              <span className="text-emerald-500 text-[10px]">✓</span>
                            )}
                          </div>
                        )}
                        {c.phone && (
                          <div className="flex items-center gap-1.5">
                            <Phone size={11} className="text-zinc-400 shrink-0" />
                            <span className="text-zinc-700">{c.phone}</span>
                            {c.phoneVerifiedAt && (
                              <span className="text-emerald-500 text-[10px]">✓</span>
                            )}
                          </div>
                        )}
                      </div>
                      {/* Edit button — visible on hover */}
                      <button
                        onClick={() => {
                          setContactForm({
                            email: c.email ?? '',
                            phone: c.phone ?? '',
                            isPrimary: c.isPrimary,
                          });
                          setShowAddContact('edit');
                        }}
                        className="opacity-0 group-hover/contact:opacity-100 transition-opacity p-1 rounded hover:bg-zinc-100 text-zinc-400 hover:text-zinc-600 shrink-0"
                        title="Edit contact"
                      >
                        <Pencil size={11} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Email Suppression — always shown so admins can verify clean status */}
          {suppressionData && (
            <Section title="Email Suppression" icon={<ShieldOff size={15} />}>
              {suppressionData.some((s) => s.isSuppressed) ? (
                <div className="space-y-2">
                  {suppressionData
                    .filter((s) => s.isSuppressed)
                    .map((s) => (
                      <div key={s.email} className="bg-red-50 border border-red-100 rounded-lg p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-zinc-800 truncate">{s.email}</p>
                            {s.reason && (
                              <p className="text-[10px] text-red-600 mt-0.5 capitalize">
                                {s.reason.replace(/_/g, ' ')}
                              </p>
                            )}
                            {s.suppressedAt && (
                              <p className="text-[10px] text-zinc-400 mt-0.5">
                                Since {formatDate(s.suppressedAt)}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => removeSuppression.mutate(s.email)}
                            className="text-[10px] text-red-500 hover:text-red-700 font-medium shrink-0"
                            title="Remove suppression"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-xs text-emerald-600 flex items-center gap-1.5">
                  <span className="text-emerald-500">✓</span> No active suppressions
                </p>
              )}
            </Section>
          )}

          {/* Consents */}
          <Section
            title="Consents"
            icon={<Shield size={15} />}
            action={
              <button
                onClick={() => setShowAddConsent(true)}
                className="p-1 rounded hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700"
              >
                <Plus size={14} />
              </button>
            }
          >
            {consents.length === 0 ? (
              <p className="text-xs text-zinc-400 italic">No consents recorded</p>
            ) : (
              <div className="space-y-2">
                {consents.map((c) => {
                  const s = CONSENT_STATUS_MAP[c.status] ?? {
                    label: c.status,
                    variant: 'default' as const,
                  };
                  return (
                    <div key={c.id} className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        <ChannelBadge channel={c.channel} />
                        {c.source && <span className="text-[10px] text-zinc-400">{c.source}</span>}
                      </div>
                      <Badge variant={s.variant}>{s.label}</Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Loyalty */}
          <Section title="Loyalty" icon={<Star size={15} />}>
            {loyaltyAccounts.length === 0 ? (
              <p className="text-xs text-zinc-400 italic">Not enrolled in any program</p>
            ) : (
              <div className="space-y-3">
                {loyaltyAccounts.map((acc) => (
                  <div key={acc.id} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Link
                        to={`/loyalty/${acc.programId}`}
                        className="text-xs font-medium text-emerald-600 hover:underline"
                      >
                        {acc.programName ?? 'Program'}
                      </Link>
                      {acc.tierCode && <Badge variant="warning">{acc.tierCode}</Badge>}
                    </div>
                    <KV label="Balance" value={`${acc.pointsBalance.toLocaleString()} pts`} />
                    <KV label="Lifetime" value={`${acc.lifetimePoints.toLocaleString()} pts`} />
                    <KV label="Enrolled" value={formatDate(acc.enrolledAt)} />
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Custom Properties (schema-driven) */}
          <Section
            title="Custom Properties"
            icon={<SlidersHorizontal size={15} />}
            action={
              <button
                onClick={() => {
                  setAttrForm({ key: '', value: '' });
                  setShowAddAttr(true);
                }}
                className="p-1 rounded hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700"
              >
                <Plus size={14} />
              </button>
            }
          >
            {(() => {
              const definedKeys = new Set(propertyDefs.map((d) => d.key));
              const schemaEntries = propertyDefs.filter((d) => d.isActive);
              const unscheduledEntries = Object.entries(attributes).filter(
                ([k]) => !definedKeys.has(k),
              );

              return (
                <div className="space-y-1">
                  {schemaEntries.length === 0 && Object.keys(attributes).length === 0 && (
                    <p className="text-xs text-zinc-400 italic">No properties defined</p>
                  )}
                  {schemaEntries.map((def) => {
                    const val = attributes[def.key];
                    return (
                      <div
                        key={def.key}
                        className="flex items-start justify-between gap-2 py-1.5 border-b border-zinc-50 last:border-0"
                      >
                        <span
                          className="text-xs text-zinc-500 shrink-0 max-w-[110px] truncate"
                          title={def.label}
                        >
                          {def.label}
                          {def.isRequired && !val && <span className="text-red-400 ml-0.5">*</span>}
                        </span>
                        {val !== undefined ? (
                          <button
                            className="text-xs text-zinc-800 font-medium text-right break-all hover:text-emerald-600 transition-colors"
                            onClick={() => {
                              setAttrForm({ key: def.key, value: val });
                              setShowAddAttr(true);
                            }}
                          >
                            {val}
                          </button>
                        ) : (
                          <button
                            className="text-xs text-zinc-300 italic hover:text-zinc-500 transition-colors"
                            onClick={() => {
                              setAttrForm({ key: def.key, value: '' });
                              setShowAddAttr(true);
                            }}
                          >
                            Set
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {unscheduledEntries.length > 0 && (
                    <details className="mt-1">
                      <summary className="text-[10px] text-zinc-400 cursor-pointer select-none hover:text-zinc-600">
                        {unscheduledEntries.length} unrecognised attribute
                        {unscheduledEntries.length > 1 ? 's' : ''}
                      </summary>
                      <div className="mt-1 space-y-0.5 pl-2 border-l border-zinc-100">
                        {unscheduledEntries.map(([k, v]) => (
                          <KV key={k} label={k} value={v} />
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              );
            })()}
          </Section>

          {/* Identities */}
          <Section
            title="Identities"
            icon={<Fingerprint size={15} />}
            action={
              <button
                onClick={() => {
                  setIdentityForm({ identityType: '', identityValue: '' });
                  setShowAddIdentity(true);
                }}
                className="p-1 rounded hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700"
              >
                <Plus size={14} />
              </button>
            }
          >
            {identities.length === 0 ? (
              <p className="text-xs text-zinc-400 italic">No linked identities</p>
            ) : (
              <div className="space-y-2">
                {identities.map((ident) => (
                  <div key={ident.id} className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-xs font-medium text-zinc-700">{ident.identityType}</p>
                      <p className="text-[11px] text-zinc-400 font-mono truncate max-w-[130px]">
                        {ident.identityValue}
                      </p>
                    </div>
                    <button
                      onClick={() => setDeleteIdentityTarget(ident)}
                      className="p-1 rounded hover:bg-red-50 text-zinc-300 hover:text-red-400"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Notes */}
          <Section
            title="Internal Notes"
            icon={<StickyNote size={15} />}
            action={
              <button
                onClick={() => setShowAddNote(true)}
                className="p-1 rounded hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700"
              >
                <Plus size={14} />
              </button>
            }
          >
            {notesLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : !notesData?.length ? (
              <p className="text-xs text-zinc-400 italic">No notes yet</p>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                {notesData.map((note) => (
                  <div key={note.id} className="bg-zinc-50 rounded-lg p-3 relative group">
                    <p className="text-xs text-zinc-700 whitespace-pre-wrap">{note.body}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-[10px] text-zinc-400">
                        {note.createdBy ? `${note.createdBy} · ` : ''}
                        {formatRelative(note.createdAt)}
                      </p>
                      <button
                        onClick={() => setDeleteNoteTarget(note)}
                        className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:text-red-500 text-zinc-300 transition-opacity"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
          {/* Tags */}
          <Section
            title="Tags"
            icon={<Tag size={15} />}
            action={
              <button
                onClick={() => setShowTagInput((v) => !v)}
                className="p-1 rounded hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700"
              >
                <Plus size={14} />
              </button>
            }
          >
            <div className="flex flex-wrap gap-1.5">
              {tags.length === 0 && !showTagInput && (
                <p className="text-xs text-zinc-400 italic">No tags</p>
              )}
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-700 text-xs px-2 py-0.5 rounded-full"
                >
                  #{tag}
                  <button
                    onClick={() => removeTag.mutate(tag)}
                    className="text-zinc-400 hover:text-red-500 transition-colors"
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
            {showTagInput && (
              <div className="mt-2 flex gap-1.5">
                <input
                  autoFocus
                  type="text"
                  value={tagInputValue}
                  onChange={(e) => setTagInputValue(e.target.value.toLowerCase())}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && tagInputValue.trim()) {
                      addTag.mutate(tagInputValue.trim());
                    } else if (e.key === 'Escape') {
                      setShowTagInput(false);
                    }
                  }}
                  placeholder="tag name…"
                  list={`tag-suggestions-${id}`}
                  className="h-7 px-2 flex-1 bg-white border border-zinc-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-zinc-900"
                />
                <datalist id={`tag-suggestions-${id}`}>
                  {allTagsData.map((t) => <option key={t} value={t} />)}
                </datalist>
                <button
                  onClick={() => { if (tagInputValue.trim()) addTag.mutate(tagInputValue.trim()); }}
                  className="text-xs bg-zinc-900 text-white px-2 py-1 rounded-lg"
                >
                  Add
                </button>
              </div>
            )}
          </Section>

          {/* Engagement Score */}
          <Section
            title="Engagement Score"
            icon={<BarChart2 size={15} />}
            action={
              <button
                onClick={() => {
                  setShowEngagementScore(true);
                  void refetchEngagement();
                }}
                className="text-xs text-zinc-400 hover:text-zinc-700 px-1"
              >
                {engagementLoading ? '…' : '↻'}
              </button>
            }
          >
            {!showEngagementScore ? (
              <button
                onClick={() => {
                  setShowEngagementScore(true);
                  void refetchEngagement();
                }}
                className="text-xs text-zinc-400 hover:text-zinc-700 italic"
              >
                Click to calculate
              </button>
            ) : engagementLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : engagementScore ? (
              <div className="space-y-2">
                {/* Score gauge */}
                <div className="flex items-center gap-3">
                  <div
                    className={`text-2xl font-bold ${
                      engagementScore.score >= 70
                        ? 'text-emerald-600'
                        : engagementScore.score >= 40
                        ? 'text-amber-500'
                        : 'text-red-500'
                    }`}
                  >
                    {engagementScore.score}
                  </div>
                  <div className="flex-1">
                    <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          engagementScore.score >= 70
                            ? 'bg-emerald-500'
                            : engagementScore.score >= 40
                            ? 'bg-amber-400'
                            : 'bg-red-400'
                        }`}
                        style={{ width: `${engagementScore.score}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-zinc-400 mt-0.5">out of 100</p>
                  </div>
                </div>
                {/* Breakdown */}
                <div className="space-y-1">
                  {([
                    ['Loyalty', engagementScore.breakdown.loyalty],
                    ['Messages', engagementScore.breakdown.messages],
                    ['Events', engagementScore.breakdown.events],
                    ['Automations', engagementScore.breakdown.automations],
                  ] as [string, number][]).map(([label, val]) => (
                    <div key={label} className="flex items-center gap-2">
                      <span className="text-[10px] text-zinc-400 w-20 shrink-0">{label}</span>
                      <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-zinc-400 rounded-full"
                          style={{ width: `${val}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-zinc-500 w-6 text-right">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </Section>
        </div>

        {/* ══ Right column — tabs ══ */}
        <div className="lg:col-span-2 space-y-4">
          {/* Tab bar */}
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="flex overflow-x-auto border-b border-zinc-100">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex items-center gap-1.5 px-4 py-3 text-xs font-medium whitespace-nowrap transition-colors border-b-2 -mb-px',
                    activeTab === tab.id
                      ? 'border-emerald-500 text-emerald-700'
                      : 'border-transparent text-zinc-500 hover:text-zinc-800',
                  )}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-5">
              {/* ─── TIMELINE ─── */}
              {activeTab === 'timeline' && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs text-zinc-500">{timelineData?.total ?? 0} events</span>
                  </div>
                  {!timelineData?.items?.length ? (
                    <p className="text-sm text-zinc-400 text-center py-8">No timeline events</p>
                  ) : (
                    <div className="space-y-4">
                      {timelineData.items.map((ev) => (
                        <div key={ev.id} className="flex gap-3">
                          <div className="flex flex-col items-center">
                            <div
                              className={cn(
                                'w-2 h-2 rounded-full mt-1 shrink-0',
                                CATEGORY_COLORS[ev.eventCategory]
                                  ?.replace('text-', 'bg-')
                                  .split(' ')[0] ?? 'bg-zinc-300',
                              )}
                            />
                            <div className="w-px flex-1 bg-zinc-100 mt-1" />
                          </div>
                          <div className="pb-4 flex-1 min-w-0">
                            <div className="flex items-start gap-2 flex-wrap">
                              <p className="text-sm font-medium text-zinc-800">{ev.title}</p>
                              <span
                                className={cn(
                                  'text-[10px] font-medium px-1.5 py-0.5 rounded-full',
                                  CATEGORY_COLORS[ev.eventCategory] ?? 'bg-zinc-100 text-zinc-500',
                                )}
                              >
                                {ev.eventCategory}
                              </span>
                            </div>
                            {ev.description && (
                              <p className="text-xs text-zinc-500 mt-0.5">{ev.description}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <code className="text-[10px] text-zinc-400 bg-zinc-50 px-1 rounded">
                                {ev.eventType}
                              </code>
                              <span className="text-[10px] text-zinc-400">
                                {formatRelative(ev.occurredAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {(timelineData?.total ?? 0) > pagLimit && (
                    <Pagination
                      page={timelinePage}
                      totalPages={Math.ceil((timelineData?.total ?? 0) / pagLimit)}
                      onPageChange={setTimelinePage}
                    />
                  )}
                </>
              )}

              {/* ─── LEDGER ─── */}
              {activeTab === 'ledger' && (
                <>
                  {!ledgerData?.items?.length ? (
                    <p className="text-sm text-zinc-400 text-center py-8">No ledger entries</p>
                  ) : (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-zinc-400 border-b border-zinc-100">
                          <th className="pb-2 text-left font-medium">Date</th>
                          <th className="pb-2 text-left font-medium">Type</th>
                          <th className="pb-2 text-right font-medium">Points</th>
                          <th className="pb-2 text-right font-medium">Balance</th>
                          <th className="pb-2 text-left font-medium">Note</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-50">
                        {ledgerData.items.map((entry) => (
                          <tr key={entry.id}>
                            <td className="py-2 text-zinc-500">{formatDate(entry.occurredAt)}</td>
                            <td className="py-2">
                              <Badge
                                variant={
                                  entry.type === 'earned' || entry.type === 'adjusted'
                                    ? 'success'
                                    : 'danger'
                                }
                              >
                                {entry.type}
                              </Badge>
                            </td>
                            <td
                              className={cn(
                                'py-2 text-right font-medium',
                                entry.points >= 0 ? 'text-emerald-600' : 'text-red-500',
                              )}
                            >
                              {entry.points >= 0 ? '+' : ''}
                              {entry.points}
                            </td>
                            <td className="py-2 text-right text-zinc-600">{entry.balanceAfter}</td>
                            <td className="py-2 text-zinc-400 truncate max-w-[160px]">
                              {entry.description}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <div className="flex items-center justify-between mt-4">
                    <button
                      disabled={ledgerPage <= 1}
                      onClick={() => setLedgerPage((p) => p - 1)}
                      className="text-xs text-zinc-500 hover:text-zinc-800 disabled:opacity-30"
                    >
                      ← Prev
                    </button>
                    <span className="text-xs text-zinc-400">
                      Page {ledgerPage}
                      {ledgerData?.total != null && (
                        <> of {Math.ceil(ledgerData.total / pagLimit)}</>
                      )}
                    </span>
                    <button
                      disabled={
                        ledgerData?.total != null
                          ? ledgerPage >= Math.ceil(ledgerData.total / pagLimit)
                          : (ledgerData?.items?.length ?? 0) < pagLimit
                      }
                      onClick={() => setLedgerPage((p) => p + 1)}
                      className="text-xs text-zinc-500 hover:text-zinc-800 disabled:opacity-30"
                    >
                      Next →
                    </button>
                  </div>
                </>
              )}

              {/* ─── COUPONS ─── */}
              {activeTab === 'coupons' && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs text-zinc-500">
                      {couponsData?.length ?? 0} coupons
                    </span>
                    <Button size="sm" variant="secondary" onClick={() => setShowIssueCoupon(true)}>
                      <Plus size={12} className="mr-1" /> Issue Coupon
                    </Button>
                  </div>
                  {!couponsData?.length ? (
                    <p className="text-sm text-zinc-400 text-center py-8">No coupons issued</p>
                  ) : (
                    <div className="space-y-2">
                      {couponsData.map((c) => (
                        <div
                          key={c.id}
                          className="flex items-center justify-between gap-3 bg-zinc-50 rounded-lg p-3"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <code className="text-xs font-mono bg-white border border-zinc-200 px-2 py-0.5 rounded shrink-0">
                              {c.code}
                            </code>
                            <div className="min-w-0">
                              <p className="text-xs text-zinc-700 font-medium truncate">
                                {c.type} — {c.value.amount}
                                {c.value.unit === 'percent'
                                  ? '%'
                                  : c.value.unit === 'fixed'
                                    ? ' off'
                                    : ' free'}
                              </p>
                              {c.expiresAt && (
                                <p className="text-[10px] text-zinc-400">
                                  Expires {formatDate(c.expiresAt)}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge
                              variant={
                                c.status === 'issued'
                                  ? 'success'
                                  : c.status === 'used'
                                    ? 'default'
                                    : 'danger'
                              }
                            >
                              {c.status}
                            </Badge>
                            {c.status === 'issued' && (
                              <button
                                onClick={() => setRevokeCouponTarget(c)}
                                className="text-[10px] text-red-500 hover:text-red-700 font-medium"
                              >
                                Revoke
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* ─── SEGMENTS ─── */}
              {activeTab === 'segments' && (
                <>
                  {!segments.length ? (
                    <p className="text-sm text-zinc-400 text-center py-8">
                      Not a member of any segment
                    </p>
                  ) : (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-zinc-400 border-b border-zinc-100">
                          <th className="pb-2 text-left font-medium">Segment</th>
                          <th className="pb-2 text-left font-medium">Type</th>
                          <th className="pb-2 text-left font-medium">Entered</th>
                          <th className="pb-2" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-50">
                        {segments.map((seg) => (
                          <tr key={seg.segmentId}>
                            <td className="py-2 font-medium text-zinc-800">{seg.name}</td>
                            <td className="py-2">
                              <Badge variant={seg.type === 'dynamic' ? 'info' : 'default'}>
                                {seg.type}
                              </Badge>
                            </td>
                            <td className="py-2 text-zinc-500">{formatDate(seg.enteredAt)}</td>
                            <td className="py-2 text-right">
                              <Link
                                to={`/segments/${seg.segmentId}`}
                                className="text-zinc-400 hover:text-emerald-600"
                              >
                                <ChevronRight size={14} />
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}

              {/* ─── MESSAGES ─── */}
              {activeTab === 'messages' && (
                <>
                  {!messagesData?.items?.length ? (
                    <p className="text-sm text-zinc-400 text-center py-8">No messages sent</p>
                  ) : (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-zinc-400 border-b border-zinc-100">
                          <th className="pb-2 text-left font-medium">Date</th>
                          <th className="pb-2 text-left font-medium">Channel</th>
                          <th className="pb-2 text-left font-medium">Subject / Recipient</th>
                          <th className="pb-2 text-left font-medium">Provider</th>
                          <th className="pb-2 text-left font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-50">
                        {messagesData.items.map((msg) => (
                          <tr key={msg.id}>
                            <td className="py-2 text-zinc-500 whitespace-nowrap">
                              {formatDate(msg.requestedAt)}
                            </td>
                            <td className="py-2">
                              <ChannelBadge channel={msg.channel} />
                            </td>
                            <td className="py-2 text-zinc-700 max-w-[180px] truncate">
                              {msg.subject ?? msg.recipientAddress}
                            </td>
                            <td className="py-2 text-zinc-400">{msg.providerName}</td>
                            <td className="py-2">
                              <Badge
                                variant={
                                  msg.status === 'sent'
                                    ? 'success'
                                    : msg.status === 'failed'
                                      ? 'danger'
                                      : 'default'
                                }
                              >
                                {msg.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {(messagesData?.total ?? 0) > 25 && (
                    <Pagination
                      page={messagesPage}
                      totalPages={Math.ceil((messagesData?.total ?? 0) / pagLimit)}
                      onPageChange={setMessagesPage}
                    />
                  )}
                </>
              )}

              {/* ─── EVENTS ─── */}
              {activeTab === 'events' && (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <Input
                      placeholder="Filter by event type…"
                      value={eventTypeFilter}
                      onChange={(e) => {
                        setEventTypeFilter(e.target.value);
                        setEventsPage(1);
                      }}
                      className="max-w-xs"
                    />
                    {eventTypeFilter && (
                      <button
                        onClick={() => {
                          setEventTypeFilter('');
                          setEventsPage(1);
                        }}
                        className="text-xs text-zinc-400 hover:text-zinc-700"
                      >
                        Clear
                      </button>
                    )}
                    <span className="text-xs text-zinc-400 ml-auto">
                      {eventsData?.total ?? 0} events
                    </span>
                  </div>
                  {!eventsData?.items?.length ? (
                    <p className="text-sm text-zinc-400 text-center py-8">No events</p>
                  ) : (
                    <div className="space-y-2">
                      {eventsData.items.map((ev) => (
                        <EventRow key={ev.id} ev={ev} />
                      ))}
                    </div>
                  )}
                  {(eventsData?.total ?? 0) > 25 && (
                    <Pagination
                      page={eventsPage}
                      totalPages={Math.ceil((eventsData?.total ?? 0) / pagLimit)}
                      onPageChange={setEventsPage}
                    />
                  )}
                </>
              )}

              {/* ─── AUTOMATIONS ─── */}
              {activeTab === 'automations' && (
                <>
                  {!automationsData?.items?.length ? (
                    <p className="text-sm text-zinc-400 text-center py-8">
                      No automation instances
                    </p>
                  ) : (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-zinc-400 border-b border-zinc-100">
                          <th className="pb-2 text-left font-medium">Automation</th>
                          <th className="pb-2 text-left font-medium">Status</th>
                          <th className="pb-2 text-left font-medium">Started</th>
                          <th className="pb-2 text-left font-medium">Ended</th>
                          <th className="pb-2 text-left font-medium">Step</th>
                          <th className="pb-2" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-50">
                        {automationsData.items.map((inst) => {
                          const st = INSTANCE_STATUS[inst.status] ?? {
                            label: inst.status,
                            cls: 'bg-zinc-100 text-zinc-500',
                          };
                          return (
                            <tr key={inst.instanceId}>
                              <td className="py-2 font-medium text-zinc-800 max-w-[160px] truncate">
                                {inst.automationName}
                              </td>
                              <td className="py-2">
                                <span
                                  className={cn(
                                    'text-[10px] font-medium px-1.5 py-0.5 rounded-full',
                                    st.cls,
                                  )}
                                >
                                  {st.label}
                                </span>
                              </td>
                              <td className="py-2 text-zinc-500 whitespace-nowrap">
                                {formatDate(inst.startedAt)}
                              </td>
                              <td className="py-2 text-zinc-500 whitespace-nowrap">
                                {inst.endedAt ? formatDate(inst.endedAt) : '—'}
                              </td>
                              <td className="py-2 text-zinc-400 truncate max-w-[120px]">
                                <code className="text-[10px]">{inst.currentStep ?? '—'}</code>
                              </td>
                              <td className="py-2 text-right">
                                <Link
                                  to={`/automations/${inst.automationId}`}
                                  className="text-zinc-400 hover:text-emerald-600"
                                >
                                  <ChevronRight size={14} />
                                </Link>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  {(automationsData?.total ?? 0) > 25 && (
                    <Pagination
                      page={automationsPage}
                      totalPages={Math.ceil((automationsData?.total ?? 0) / pagLimit)}
                      onPageChange={setAutomationsPage}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ══ Modals ══ */}

      {/* Edit profile — 3-tab modal */}
      <Modal open={showEditProfile} onClose={() => setShowEditProfile(false)} title="Edit Customer">
        {/* Tab bar */}
        <div className="flex gap-1 border-b border-zinc-100 -mx-6 px-6 mb-4">
          {(
            [
              { id: 'personal', label: 'Personal' },
              { id: 'location', label: 'Location & Context' },
              { id: 'professional', label: 'Professional' },
            ] as const
          ).map((t) => (
            <button
              key={t.id}
              onClick={() => setEditProfileTab(t.id)}
              className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
                editProfileTab === t.id
                  ? 'border-zinc-900 text-zinc-900'
                  : 'border-transparent text-zinc-500 hover:text-zinc-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {editProfileTab === 'personal' && (
            <>
              <Input
                label="First name"
                value={editProfileForm.firstName}
                onChange={(e) => setEditProfileForm((f) => ({ ...f, firstName: e.target.value }))}
              />
              <Input
                label="Last name"
                value={editProfileForm.lastName}
                onChange={(e) => setEditProfileForm((f) => ({ ...f, lastName: e.target.value }))}
              />
              <Select
                label="Status"
                value={editProfileForm.status}
                onChange={(e) => setEditProfileForm((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="blocked">Blocked</option>
              </Select>
              <Input
                label="Date of birth"
                type="date"
                value={editProfileForm.dateOfBirth}
                onChange={(e) => setEditProfileForm((f) => ({ ...f, dateOfBirth: e.target.value }))}
              />
              <Input
                label="Gender"
                placeholder="e.g. male, female, non-binary"
                value={editProfileForm.gender}
                onChange={(e) => setEditProfileForm((f) => ({ ...f, gender: e.target.value }))}
              />
              <div>
                <label className="block text-xs font-medium text-zinc-700 mb-1">Avatar</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="https://…"
                    value={editProfileForm.avatarUrl}
                    onChange={(e) => setEditProfileForm((f) => ({ ...f, avatarUrl: e.target.value }))}
                    className="flex-1 h-9 px-3 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  />
                  <label className="h-9 px-3 flex items-center gap-1.5 bg-zinc-100 hover:bg-zinc-200 rounded-lg text-xs font-medium text-zinc-600 cursor-pointer transition-colors">
                    <Upload size={13} />
                    Upload
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="sr-only"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const { data } = await api.post(
                            `/api/v1/customers/${id}/avatar-upload-url`,
                            null,
                            { params: { contentType: file.type } },
                          );
                          await fetch(data.uploadUrl, { method: 'PUT', body: file });
                          setEditProfileForm((f) => ({ ...f, avatarUrl: data.avatarUrl }));
                          toast.success('Avatar uploaded — save to apply');
                        } catch (err) {
                          toast.error(extractError(err, 'Upload failed'));
                        }
                      }}
                    />
                  </label>
                </div>
              </div>
            </>
          )}

          {editProfileTab === 'location' && (
            <>
              <Input
                label="Country (ISO 2)"
                placeholder="e.g. IT, US, GB"
                maxLength={2}
                value={editProfileForm.country}
                onChange={(e) =>
                  setEditProfileForm((f) => ({ ...f, country: e.target.value.toUpperCase() }))
                }
              />
              <Input
                label="City"
                value={editProfileForm.city}
                onChange={(e) => setEditProfileForm((f) => ({ ...f, city: e.target.value }))}
              />
              <Input
                label="Language (IETF)"
                placeholder="e.g. en, it, fr"
                value={editProfileForm.language}
                onChange={(e) => setEditProfileForm((f) => ({ ...f, language: e.target.value }))}
              />
              <Input
                label="Timezone"
                placeholder="e.g. Europe/Rome"
                value={editProfileForm.timezone}
                onChange={(e) => setEditProfileForm((f) => ({ ...f, timezone: e.target.value }))}
              />
              <Input
                label="Acquisition source"
                placeholder="e.g. organic, referral, paid_search"
                value={editProfileForm.acquisitionSource}
                onChange={(e) =>
                  setEditProfileForm((f) => ({ ...f, acquisitionSource: e.target.value }))
                }
              />
              <Input
                label="Acquisition date"
                type="date"
                value={editProfileForm.acquisitionDate}
                onChange={(e) =>
                  setEditProfileForm((f) => ({ ...f, acquisitionDate: e.target.value }))
                }
              />
              <Input
                label="Address line 1"
                placeholder="Via Roma 1"
                value={editProfileForm.addressLine1}
                onChange={(e) => setEditProfileForm((f) => ({ ...f, addressLine1: e.target.value }))}
              />
              <Input
                label="Address line 2"
                placeholder="Interno, scala…"
                value={editProfileForm.addressLine2}
                onChange={(e) => setEditProfileForm((f) => ({ ...f, addressLine2: e.target.value }))}
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Postal code"
                  placeholder="00100"
                  value={editProfileForm.postalCode}
                  onChange={(e) => setEditProfileForm((f) => ({ ...f, postalCode: e.target.value }))}
                />
                <Input
                  label="State / Province"
                  placeholder="RM"
                  value={editProfileForm.stateProvince}
                  onChange={(e) => setEditProfileForm((f) => ({ ...f, stateProvince: e.target.value }))}
                />
              </div>
            </>
          )}

          {editProfileTab === 'professional' && (
            <>
              <Input
                label="Company"
                value={editProfileForm.company}
                onChange={(e) => setEditProfileForm((f) => ({ ...f, company: e.target.value }))}
              />
              <Input
                label="Job title"
                value={editProfileForm.jobTitle}
                onChange={(e) => setEditProfileForm((f) => ({ ...f, jobTitle: e.target.value }))}
              />
            </>
          )}
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowEditProfile(false)}>
            Cancel
          </Button>
          <Button
            loading={updateProfile.isPending}
            onClick={() => {
              const nullIfEmpty = (v: string) => (v.trim() === '' ? null : v);
              updateProfile.mutate({
                ...editProfileForm,
                dateOfBirth: nullIfEmpty(editProfileForm.dateOfBirth),
                gender: nullIfEmpty(editProfileForm.gender),
                language: nullIfEmpty(editProfileForm.language),
                country: nullIfEmpty(editProfileForm.country),
                city: nullIfEmpty(editProfileForm.city),
                timezone: nullIfEmpty(editProfileForm.timezone),
                avatarUrl: nullIfEmpty(editProfileForm.avatarUrl),
                acquisitionSource: nullIfEmpty(editProfileForm.acquisitionSource),
                acquisitionDate: nullIfEmpty(editProfileForm.acquisitionDate),
                company: nullIfEmpty(editProfileForm.company),
                jobTitle: nullIfEmpty(editProfileForm.jobTitle),
                addressLine1: nullIfEmpty(editProfileForm.addressLine1),
                addressLine2: nullIfEmpty(editProfileForm.addressLine2),
                postalCode: nullIfEmpty(editProfileForm.postalCode),
                stateProvince: nullIfEmpty(editProfileForm.stateProvince),
              });
            }}
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>

      {/* Add / update contact */}
      <Modal
        open={showAddContact !== false}
        onClose={() => setShowAddContact(false)}
        title={showAddContact === 'edit' ? 'Update Contact' : 'Add Contact'}
      >
        <div className="space-y-3">
          <Input
            label="Email"
            type="email"
            value={contactForm.email}
            onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))}
          />
          <Input
            label="Phone"
            value={contactForm.phone}
            onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))}
          />
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowAddContact(false)}>
            Cancel
          </Button>
          <Button
            loading={upsertContact.isPending}
            onClick={() => upsertContact.mutate(contactForm)}
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>

      {/* Record consent */}
      <Modal open={showAddConsent} onClose={() => setShowAddConsent(false)} title="Record Consent">
        <div className="space-y-3">
          <Select
            label="Channel"
            value={consentForm.channel}
            onChange={(e) => setConsentForm((f) => ({ ...f, channel: e.target.value }))}
          >
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="push">Push</option>
          </Select>
          <Select
            label="Status"
            value={consentForm.status}
            onChange={(e) => setConsentForm((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="granted">Granted</option>
            <option value="revoked">Revoked</option>
            <option value="pending">Pending</option>
          </Select>
          <Input
            label="Source (optional)"
            placeholder="e.g. web_form, import"
            value={consentForm.source}
            onChange={(e) => setConsentForm((f) => ({ ...f, source: e.target.value }))}
          />
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowAddConsent(false)}>
            Cancel
          </Button>
          <Button
            loading={recordConsent.isPending}
            onClick={() => recordConsent.mutate(consentForm)}
          >
            Record
          </Button>
        </ModalFooter>
      </Modal>

      {/* Add attribute */}
      <Modal open={showAddAttr} onClose={() => setShowAddAttr(false)} title="Add Custom Attribute">
        <div className="space-y-3">
          <Input
            label="Key"
            placeholder="e.g. preferred_language"
            value={attrForm.key}
            onChange={(e) => setAttrForm((f) => ({ ...f, key: e.target.value }))}
          />
          <Input
            label="Value"
            placeholder="e.g. en"
            value={attrForm.value}
            onChange={(e) => setAttrForm((f) => ({ ...f, value: e.target.value }))}
          />
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowAddAttr(false)}>
            Cancel
          </Button>
          <Button
            loading={setAttribute.isPending}
            disabled={!attrForm.key}
            onClick={() => setAttribute.mutate(attrForm)}
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>

      {/* Add identity */}
      <Modal open={showAddIdentity} onClose={() => setShowAddIdentity(false)} title="Add Identity">
        <div className="space-y-3">
          <Input
            label="Identity type"
            placeholder="e.g. google, loyalty_card, stripe"
            value={identityForm.identityType}
            onChange={(e) => setIdentityForm((f) => ({ ...f, identityType: e.target.value }))}
          />
          <Input
            label="Identity value"
            placeholder="e.g. google|12345, CARD-9999"
            value={identityForm.identityValue}
            onChange={(e) => setIdentityForm((f) => ({ ...f, identityValue: e.target.value }))}
          />
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowAddIdentity(false)}>
            Cancel
          </Button>
          <Button
            loading={addIdentity.isPending}
            disabled={!identityForm.identityType || !identityForm.identityValue}
            onClick={() => addIdentity.mutate(identityForm)}
          >
            Add
          </Button>
        </ModalFooter>
      </Modal>

      {/* Add note */}
      <Modal open={showAddNote} onClose={() => setShowAddNote(false)} title="Add Note">
        <Textarea
          label="Note"
          rows={4}
          placeholder="Write an internal note about this customer…"
          value={noteBody}
          onChange={(e) => setNoteBody(e.target.value)}
        />
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowAddNote(false)}>
            Cancel
          </Button>
          <Button
            loading={addNote.isPending}
            disabled={!noteBody.trim()}
            onClick={() => addNote.mutate(noteBody)}
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>

      {/* Issue coupon */}
      <Modal open={showIssueCoupon} onClose={() => setShowIssueCoupon(false)} title="Issue Coupon">
        <div className="space-y-3">
          <Input
            label="Coupon type"
            placeholder="e.g. discount, birthday"
            value={couponForm.type}
            onChange={(e) => setCouponForm((f) => ({ ...f, type: e.target.value }))}
          />
          <Input
            label="Amount"
            type="number"
            placeholder="e.g. 10"
            value={couponForm.amount}
            onChange={(e) => setCouponForm((f) => ({ ...f, amount: e.target.value }))}
          />
          <Select
            label="Unit"
            value={couponForm.unit}
            onChange={(e) => setCouponForm((f) => ({ ...f, unit: e.target.value }))}
          >
            <option value="percent">Percent (%)</option>
            <option value="fixed">Fixed amount</option>
            <option value="free">Free item</option>
          </Select>
          <Input
            label="Expires in (days, optional)"
            type="number"
            placeholder="Leave blank for no expiry"
            value={couponForm.expiresInDays}
            onChange={(e) => setCouponForm((f) => ({ ...f, expiresInDays: e.target.value }))}
          />
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowIssueCoupon(false)}>
            Cancel
          </Button>
          <Button
            loading={issueCoupon.isPending}
            disabled={!couponForm.type || !couponForm.amount}
            onClick={() => issueCoupon.mutate()}
          >
            Issue
          </Button>
        </ModalFooter>
      </Modal>

      {/* Block / Unblock confirm */}
      <ConfirmDialog
        open={blockConfirm}
        title={customer.status === 'blocked' ? 'Unblock customer?' : 'Block customer?'}
        message={
          customer.status === 'blocked'
            ? 'This will restore the customer to active status.'
            : 'The customer will no longer be able to participate in automations or receive messages.'
        }
        confirmLabel={customer.status === 'blocked' ? 'Unblock' : 'Block'}
        loading={blockMutation.isPending}
        onConfirm={() => blockMutation.mutate()}
        onCancel={() => setBlockConfirm(false)}
      />

      {/* Delete identity confirm */}
      <ConfirmDialog
        open={!!deleteIdentityTarget}
        title="Remove identity?"
        message={`Remove ${deleteIdentityTarget?.identityType}: ${deleteIdentityTarget?.identityValue}?`}
        confirmLabel="Remove"
        onConfirm={() => deleteIdentityTarget && deleteIdentity.mutate(deleteIdentityTarget.id)}
        onCancel={() => setDeleteIdentityTarget(null)}
      />

      {/* Delete note confirm */}
      <ConfirmDialog
        open={!!deleteNoteTarget}
        title="Delete note?"
        message="This note will be permanently deleted."
        confirmLabel="Delete"
        onConfirm={() => deleteNoteTarget && deleteNote.mutate(deleteNoteTarget.id)}
        onCancel={() => setDeleteNoteTarget(null)}
      />

      {/* Revoke coupon confirm */}
      <ConfirmDialog
        open={!!revokeCouponTarget}
        title="Revoke coupon?"
        message={`Coupon ${revokeCouponTarget?.code} will be revoked and can no longer be used.`}
        confirmLabel="Revoke"
        onConfirm={() => revokeCouponTarget && revokeCoupon.mutate(revokeCouponTarget.id)}
        onCancel={() => setRevokeCouponTarget(null)}
      />
    </div>
  );
}

// ─── EventRow — collapsible event with payload ────────────────────────────────

function EventRow({ ev }: { ev: EventInbox }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-zinc-50 rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-zinc-100 transition-colors"
        onClick={() => setOpen((o) => !o)}
      >
        <div className="flex-1 min-w-0 flex items-center gap-3 flex-wrap">
          <code className="text-[11px] font-mono text-zinc-700 shrink-0">{ev.eventType}</code>
          <span className="text-[10px] text-zinc-400">{ev.source}</span>
          <span className="text-[10px] text-zinc-400">
            {new Date(ev.occurredAt).toLocaleString('en-GB')}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant={ev.processedAt ? 'success' : 'warning'}>
            {ev.processedAt ? 'processed' : 'pending'}
          </Badge>
          {open ? (
            <ChevronDown size={12} className="text-zinc-400" />
          ) : (
            <ChevronRight size={12} className="text-zinc-400" />
          )}
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3">
          <pre className="text-[10px] text-zinc-600 bg-white border border-zinc-200 rounded p-2 overflow-x-auto max-h-48">
            {JSON.stringify(ev.payloadJson, null, 2)}
          </pre>
          {ev.processingError && (
            <p className="text-[10px] text-red-500 mt-1">Error: {ev.processingError}</p>
          )}
        </div>
      )}
    </div>
  );
}
